import styled from 'styled-components';
import {QUOTE_DOC_COLORS as C} from '../pdf/quotePdfStyles';
import type {QuoteThemeTokens} from '../theme/themeTokens';
import type {QuoteFont} from '../resolver/types';

// CSS stacks approximating the PDF's built-in faces, so the live preview reads like the
// document the download produces.
export const previewFontStack = (font: QuoteFont): string => {
	if (font === 'times') return "'Times New Roman', Times, serif";
	if (font === 'courier') return "'Courier New', Courier, monospace";
	return 'Helvetica, Arial, sans-serif';
};

// One A4 sheet at the PDF's native point size (595×842), so every measurement below can be
// copied 1:1 from the PDF stylesheet. The pane scrolls; each sheet keeps its own chrome.
export const PageSheet = styled.section<{$font: QuoteFont; $cover?: boolean}>`
	position: relative;
	width: 595px;
	min-height: 842px;
	margin: 0 auto 24px;
	padding: ${p => (p.$cover ? '0 0 72px' : '84px 48px 72px')};
	background: #ffffff;
	box-shadow: 0 1px 3px rgba(29, 32, 70, 0.14), 0 6px 18px rgba(29, 32, 70, 0.08);
	font-family: ${p => previewFontStack(p.$font)};
	font-size: 10px;
	color: ${C.ink};
	box-sizing: border-box;
	display: flex;
	flex-direction: column;
`;

export const Band = styled.div`
	position: absolute;
	top: 28px;
	left: 48px;
	right: 48px;
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding-bottom: 10px;
	border-bottom: 1px solid ${C.border};
`;

export const BandRun = styled.div`
	display: flex;
	align-items: center;
`;

export const BandLogoImage = styled.img`
	height: 14px;
	max-width: 42px;
	object-fit: contain;
	object-position: left;
	margin-right: 8px;
`;

export const BandLogoWord = styled.span`
	font-size: 9px;
	font-weight: 700;
	color: ${C.secondary};
	margin-right: 8px;
`;

export const BandRunText = styled.span`
	font-size: 8px;
	color: ${C.muted};
`;

export const BandConfidential = styled.span`
	font-size: 7px;
	letter-spacing: 1.1px;
	text-transform: uppercase;
	color: ${C.muted};
`;

export const PageContent = styled.div`
	flex: 1;
	display: flex;
	flex-direction: column;
`;

export const Foot = styled.div`
	position: absolute;
	bottom: 26px;
	left: 48px;
	right: 48px;
	display: flex;
	justify-content: space-between;
	border-top: 1px solid ${C.border};
	padding-top: 8px;
	font-size: 8px;
	color: ${C.muted};
`;

export const Section = styled.div`
	margin-bottom: 22px;
`;

/* The rule lives here rather than on the heading text: the design runs it the full width of the
   column, and a flex item would only underline the words. The PDF reads the same two tokens. */
export const SectionLabelRow = styled.div<{$tokens?: QuoteThemeTokens}>`
	display: flex;
	align-items: center;
	margin-bottom: 13px;
	${p =>
		p.$tokens?.headingRuleColor
			? `padding-bottom: ${p.$tokens.headingPaddingBottom}px; border-bottom: 1px solid ${p.$tokens.headingRuleColor};`
			: ''}
`;

export const SectionLabelBar = styled.span<{$brand: string}>`
	width: 16px;
	height: 2px;
	border-radius: 1px;
	margin-right: 8px;
	background: ${p => p.$brand};
`;

/* A real heading element, so the preview carries the same document outline a reader gets from the
   PDF's bookmarks rather than a page of undifferentiated spans. */
export const SectionLabelText = styled.h2<{$brand: string; $tokens?: QuoteThemeTokens}>`
	margin: 0;
	/* The app styles headings globally, so an element-matching rule would outrank the inherited
	   family; naming the family here keeps the heading on the theme's face. */
	font-family: ${p => (p.$tokens ? previewFontStack(p.$tokens.headingFont) : 'inherit')};
	font-size: ${p => p.$tokens?.headingSize ?? 9}px;
	font-weight: ${p => p.$tokens?.headingWeight ?? 700};
	letter-spacing: ${p => p.$tokens?.headingLetterSpacing ?? 1.3}px;
	text-transform: ${p => p.$tokens?.headingTransform ?? 'uppercase'};
	color: ${p => p.$brand};
	min-width: 0;
	/* An unbroken title has no space to wrap at, and without this it runs off the side of the
	   sheet rather than onto the next line. */
	overflow-wrap: anywhere;
`;

export const Paragraph = styled.p`
	font-size: 10px;
	line-height: 1.65;
	margin: 0 0 9px;
	color: ${C.body};
`;

// ---- Cover -------------------------------------------------------------------
export const CoverContent = styled.div`
	flex: 1;
	display: flex;
	flex-direction: column;
`;

/**
 * The cover band: full-bleed, the logo pinned to its top and the title block to its bottom. It is
 * the HTML twin of the PDF's coverBand and reads the same tokens.
 */
export const CoverBand = styled.div<{$tokens?: QuoteThemeTokens}>`
	flex: none;
	height: ${p => p.$tokens?.coverHeight ?? 294}px;
	padding: 42px;
	box-sizing: border-box;
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	background: ${p => p.$tokens?.coverBackground ?? '#ffffff'};
	color: ${p => p.$tokens?.coverForeground ?? C.ink};
	${p => (p.$tokens?.coverRuleColor ? `border-bottom: 1px solid ${p.$tokens.coverRuleColor};` : '')}
`;

export const CoverLogoImage = styled.img`
	height: 24px;
	max-width: 72px;
	object-fit: contain;
	object-position: left;
`;

export const CoverLogoWord = styled.div`
	font-size: 14px;
	font-weight: 700;
`;

export const CoverTitleBlock = styled.div<{$tokens?: QuoteThemeTokens}>`
	display: flex;
	flex-direction: column;
	align-items: ${p => (p.$tokens?.coverAlign === 'center' ? 'center' : 'flex-start')};
	text-align: ${p => (p.$tokens?.coverAlign === 'center' ? 'center' : 'left')};
`;

export const CoverEyebrow = styled.div<{$tokens?: QuoteThemeTokens}>`
	font-size: 9.75px;
	letter-spacing: ${p => p.$tokens?.coverKickerLetterSpacing ?? 2.2}px;
	text-transform: uppercase;
	opacity: 0.75;
	margin-bottom: 10.5px;
`;

export const CoverTitle = styled.h1<{$tokens?: QuoteThemeTokens}>`
	/* The app styles headings globally; a matching h1 rule outranks the font-family inherited from
	   PageSheet, so without this the document's most prominent line ignores the font picker while
	   the body text follows it. */
	font-family: ${p => (p.$tokens ? previewFontStack(p.$tokens.headingFont) : 'inherit')};
	font-size: ${p => p.$tokens?.coverTitleSize ?? 34.5}px;
	font-weight: ${p => p.$tokens?.headingWeight ?? 700};
	line-height: 1.08;
	letter-spacing: -0.4px;
	margin: 0 0 10.5px;
	color: inherit;
`;

export const CoverFor = styled.div`
	font-size: 11.25px;
	opacity: 0.8;
`;

export const CoverBody = styled.div`
	flex: 1;
	padding: 36px 42px;
`;

export const CoverMeta = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 21px 24px;
`;

export const CoverMetaCell = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
`;

export const CoverMetaLabel = styled.div`
	font-size: 7.9px;
	letter-spacing: 1.2px;
	text-transform: uppercase;
	color: #8b8b9b;
`;

export const CoverMetaValue = styled.div`
	font-size: 10.9px;
	color: #232334;
`;

// ---- Estimate table ----------------------------------------------------------
export const TableHeadRow = styled.div<{$tokens?: QuoteThemeTokens}>`
	display: flex;
	border-bottom: 1.5px solid ${p => p.$tokens?.ruleColor ?? C.border};
	background: ${p => p.$tokens?.tableHeadBackground ?? 'transparent'};
	margin: 0 ${p => (p.$tokens ? '-8px' : '0')};
	padding: ${p => (p.$tokens ? '7px 8px' : '0 0 7px')};
`;

export const Th = styled.span<{$num?: boolean; $tokens?: QuoteThemeTokens}>`
	font-size: 8px;
	font-weight: 700;
	letter-spacing: 0.8px;
	text-transform: uppercase;
	color: ${p => p.$tokens?.tableHeadColor ?? C.muted};
	text-align: ${p => (p.$num ? 'right' : 'left')};
`;

export const ColLabel = styled.span`
	flex: 1;
	padding-right: 8px;
	min-width: 0;
`;

export const ColNum = styled.span`
	width: 74px;
	flex: none;
	text-align: right;
`;

export const PhaseRow = styled.div`
	display: flex;
	align-items: flex-end;
	padding: 12px 0 3px;
`;

export const PhaseIndex = styled.span<{$brand: string}>`
	font-size: 7.5px;
	font-weight: 700;
	letter-spacing: 0.8px;
	text-transform: uppercase;
	margin-right: 8px;
	color: ${p => p.$brand};
`;

/* Set apart by voice, not size: the accent, uppercase and tracked, at the rows' own body size. */
export const PhaseName = styled.span<{$brand: string}>`
	font-size: 10px;
	font-weight: 700;
	letter-spacing: 0.8px;
	text-transform: uppercase;
	color: ${p => p.$brand};
`;

export const PhaseSubtotal = styled.span`
	font-size: 10.5px;
	font-weight: 700;
`;

export const DeliverableRow = styled.div`
	display: flex;
	align-items: center;
	padding: 11px 0 2px 10px;
`;

export const DeliverableLabel = styled.span<{$brand: string}>`
	font-size: 7px;
	font-weight: 700;
	letter-spacing: 0.9px;
	text-transform: uppercase;
	margin-right: 8px;
	color: ${p => p.$brand};
`;

export const DeliverableName = styled.span`
	font-size: 8.5px;
	font-weight: 700;
`;

export const ItemRow = styled.div`
	display: flex;
	padding: 8px 0;
	border-bottom: 1px solid ${C.border};
`;

export const ItemName = styled.span`
	font-size: 10px;
`;

export const ItemNum = styled.span`
	font-size: 10px;
`;

export const ItemTotal = styled.span`
	font-size: 10px;
	font-weight: 700;
`;

export const ExpenseTag = styled.span`
	font-size: 7px;
	font-weight: 700;
	letter-spacing: 0.9px;
	text-transform: uppercase;
	color: ${C.muted};
	margin-right: 7px;
`;

/* The design sets a phase's subtotal apart on its own tinted band. */
export const PhaseSubtotalRow = styled.div`
	display: flex;
	padding: 9px 0;
	background: #f5f3f9;
	border-top: 1px solid ${C.border};
	border-bottom: 1px solid ${C.border};
`;

export const ExpensesTotalLabel = styled.span`
	font-size: 10px;
	font-weight: 700;
`;

export const RollupRow = styled.div`
	display: flex;
	padding: 10px 0;
	border-bottom: 1px solid ${C.border};
`;

export const RollupName = styled.span`
	font-size: 10.5px;
	font-weight: 700;
`;

export const TotalsWrap = styled.div`
	display: flex;
	justify-content: flex-end;
	margin-top: 18px;
`;

export const TotalsGrid = styled.div`
	width: 250px;
`;

export const TotalsRow = styled.div`
	display: flex;
	justify-content: space-between;
	margin-bottom: 7px;
	font-size: 10px;
`;

export const TotalsLabel = styled.span`
	color: ${C.secondary};
`;

export const TotalsGrandRow = styled.div<{$tokens?: QuoteThemeTokens}>`
	display: flex;
	justify-content: space-between;
	border-top: 1.5px solid ${p => p.$tokens?.grandTotalRuleColor ?? C.ink};
	padding-top: 9px;
	margin-top: 3px;
	font-size: 12px;
	font-weight: 700;
`;

export const TotalsGrandValue = styled.span<{$brand: string}>`
	color: ${p => p.$brand};
`;

// ---- Terms -------------------------------------------------------------------
export const TermRow = styled.div`
	display: flex;
	margin-bottom: 13px;
`;

export const TermNumber = styled.span<{$brand: string}>`
	width: 16px;
	flex: none;
	font-size: 11px;
	font-weight: 700;
	color: ${p => p.$brand};
`;

export const TermBody = styled.div`
	flex: 1;
	min-width: 0;
`;

export const TermHeading = styled.div`
	font-size: 10px;
	font-weight: 700;
	margin-bottom: 3px;
`;

export const TermText = styled.p`
	font-size: 9.5px;
	line-height: 1.6;
	margin: 0;
	color: ${C.body};
`;

// ---- Agreement / signatures ---------------------------------------------------
export const SignGrid = styled.div`
	display: flex;
	margin-top: 28px;
`;

export const SignParty = styled.div<{$first?: boolean}>`
	flex: 1;
	margin-right: ${p => (p.$first ? '36px' : '0')};
`;

export const SignOrg = styled.div`
	font-size: 11px;
	font-weight: 700;
	margin-bottom: 18px;
`;

export const SignField = styled.div`
	margin-top: 22px;
`;

export const SignSignatureSpace = styled.div`
	height: 22px;
`;

export const SignLine = styled.div`
	border-bottom: 1px solid ${C.ink};
	margin-bottom: 5px;
`;

export const SignLabel = styled.div`
	font-size: 8px;
	letter-spacing: 0.6px;
	text-transform: uppercase;
	color: ${C.muted};
`;

export const SignNote = styled.p`
	margin: 28px 0 0;
	font-size: 8.5px;
	line-height: 1.5;
	color: ${C.muted};
`;
